import DoneIcon from '@mui/icons-material/Done';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Box,
  Dialog,
  DialogContent,
  Stack,
  FormControlLabel,
  TextField,
  RadioGroup,
  Radio,
  Typography,
  FormControl,
  MenuItem,
  Select,
  SelectChangeEvent,
  Grid,
  DialogTitle,
  IconButton,
  CircularProgress,
  Tooltip,
  Chip,
} from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import { FC, Key, useContext, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import InfoIcon from '@mui/icons-material/Info';

import { InterviewBlock } from '@features/enterpriseEvents';
import { useCreateInterviewBlock } from '@features/enterpriseEvents/hooks/useCreateInterviewBlock';
import { useUpdateInterviewBlock } from '@features/enterpriseEvents/hooks/useUpdateInterviewBlock';

import { useCreateInterviewBlockTime } from '@features/enterpriseEvents/hooks/useCreateInterviewBlockTime';
import {
  authContext,
  useGetJobPostings,
} from '@staffedupusa/shared-components';

import CloseIcon from '@mui/icons-material/Close';

import SelectDateTimeForm from '../SelectDateTimeForm/SelectDateTimeForm';

type CompanyDetail = {
  name: string;
  company_id: number;
} | null;

type InterviewBlockModalProps = {
  isOpen: boolean;
  handleClose: () => void;
  interviewBlock?: InterviewBlock;
  onSuccess?: () => void;
  companyDetail?: CompanyDetail;
};
const InterviewBlockModal: FC<InterviewBlockModalProps> = ({
  isOpen,
  handleClose,
  interviewBlock,
  onSuccess = () => {},
  companyDetail,
}) => {
  const calculateTimeDifference = (
    slotStartTime: string,
    slotEndTime: string,
  ) => {
    try {
      const startTime = new Date(Date.parse(slotStartTime));
      const startTimestamp = startTime.getTime();
      const endTime = new Date(Date.parse(slotEndTime));
      const endTimestamp = endTime.getTime();
      const differenceInMilliseconds = endTimestamp - startTimestamp;
      const differenceInMinutes = Math.floor(
        differenceInMilliseconds / (1000 * 60),
      );
      return differenceInMinutes;
    } catch (error) {
      return '15';
    }
  };

  const [selectedDays, setSelectedDays] = useState(
    interviewBlock ? interviewBlock.repeat?.days?.split(',') ?? ['2'] : ['2'],
  );

  const { authData } = useContext(authContext);

  const [repeatEnd, setRepeatEnd] = useState<string>(
    interviewBlock
      ? interviewBlock.repeat?.ends_type === 0
        ? 'never'
        : interviewBlock.repeat?.ends_type === 1
          ? 'on'
          : interviewBlock.repeat?.ends_type === 2
            ? 'after'
            : 'never'
      : 'never',
  );
  const [repeateDate, setRepeatDate] = useState<Dayjs | null>(
    interviewBlock && interviewBlock.repeat?.ends_type === 1
      ? dayjs(interviewBlock.repeat.ends_on)
      : null,
  );
  const [repeateOccurence, setRepeatOccurence] = useState<string | null>(
    interviewBlock && interviewBlock.repeat?.ends_type === 2
      ? interviewBlock.repeat.ends_on
      : null,
  );
  const [repeat, setRepeat] = useState(
    interviewBlock ? interviewBlock.repeat?.repeat : false,
  );
  const [showRepeatModal, setShowRepeatModal] = useState(false);
  const { jobPostingsData, isLoadingJobPostings } = useGetJobPostings(
    interviewBlock
      ? interviewBlock.company.id
      : companyDetail
        ? companyDetail?.company_id
        : null,
  );
  const [previousJobIds, setPreviousJobIds] = useState(
    interviewBlock
      ? interviewBlock.jobs?.map((item) => item.job_listing.id)
      : [],
  );
  const [slotTime, setSlotTime] = useState(
    interviewBlock?.slots && interviewBlock.slots?.length > 0
      ? `${calculateTimeDifference(interviewBlock?.slots?.[0]?.slot_start_time, interviewBlock?.slots?.[0]?.slot_end_time)}`
      : '15',
  );
  // const [selectedItems, setSelectedItems] = useState(interviewBlock ? interviewBlock.jobs?.map(item => item.job_listing) : (jobPostingsData ? jobPostingsData.object?.filter((item: any) => item.status !== 1) : []));
  const [selectedItems, setSelectedItems] = useState(() => {
    if (interviewBlock && jobPostingsData) {
      const filteredItems = jobPostingsData.object?.filter(
        (item: { id: any; status: number }) =>
          interviewBlock.jobs &&
          interviewBlock.jobs.some(
            (job) =>
              job.job_listing.id === item.id &&
              item.status !== 1 &&
              item.status !== 4,
          ),
      );
      return filteredItems || [];
    } else if (jobPostingsData) {
      return (
        jobPostingsData.object?.filter(
          (item: any) => item.status !== 1 && item.status !== 4,
        ) || []
      );
    } else {
      return [];
    }
  });

  const [value, setValue] = useState(
    interviewBlock?.slots && interviewBlock.slots?.length > 0
      ? 'time'
      : interviewBlock
        ? 'number'
        : 'time',
  );
  const [startTimestamp, setStartTimestamp] = useState<number | null>(
    interviewBlock?.dt_start_time
      ? dayjs(interviewBlock.dt_start_time).valueOf() / 1000
      : null,
  );

  const [endTimestamp, setEndTimestamp] = useState<number | null>(
    interviewBlock?.dt_end_time
      ? dayjs(interviewBlock.dt_end_time).valueOf() / 1000
      : null,
  );

  const [formattedDate, setFormattedDate] = useState<string>(
    dayjs(interviewBlock?.dt_start_time).format('YYYY-MM-DD') ?? '',
  );
  const [isPastDateTime, setIsPastDateTime] = useState(false);
  const [numberOfSlots, setNumberOfSlots] = useState(
    interviewBlock?.max_spots ?? 4,
  );
  const [interviewBlockStartTime, setInterviewBlockStartTime] = useState<
    number | null
  >(
    interviewBlock
      ? dayjs(interviewBlock?.dt_start_time).valueOf() / 1000
      : null,
  );
  const [errorText, setErrorText] = useState('');

  const [defaultType, setDefaultType] = useState<'inPerson' | 'virtual'>(
    'inPerson',
  );

  const { createInterviewBlock, isCreatingInterviewBlock } =
    useCreateInterviewBlock({ setErrorText });

  const { createInterviewBlockTime, isCreatingInterviewBlockTime } =
    useCreateInterviewBlockTime({ setErrorText });

  const { updateInterviewBlock, isUpdatingInterviewBlock } =
    useUpdateInterviewBlock({
      setErrorText,
      interviewBlockStartTime: interviewBlockStartTime!,
    });

  const updateNumberOfSlots = (value: number) => {
    if (value > 0) {
      setNumberOfSlots(value);
    } else {
      setNumberOfSlots(1);
    }
  };

  const handleJobChange = (event: any) => {
    const value = event.target.value;
    if (value.includes('select-all')) {
      handleSelectAll(); // Call select all/deselect all logic if "Select All" is clicked
    } else {
      setSelectedItems(value); // Otherwise, handle individual selections
    }
  };

  // Handle select/deselect all functionality
  const handleSelectAll = () => {
    if (
      selectedItems.length ===
      jobPostingsData?.object?.filter(
        (item: any) => item.status !== 1 && item.status !== 4,
      ).length
    ) {
      setSelectedItems([]); // Deselect all if all are selected
    } else {
      setSelectedItems(
        jobPostingsData?.object?.filter(
          (item: any) => item.status !== 1 && item.status !== 4,
        ),
      );
    }
  };

  const reset = () => {
    setStartTimestamp(null);
    setEndTimestamp(null);
    setFormattedDate('');
    setNumberOfSlots(4);
    setSlotTime('15');
    setValue('time');
    setRepeat(false);
    setErrorText('');

    setShowRepeatModal(false);
    setRepeatOccurence(null);
    setRepeatDate(null);
    setSelectedDays(['1']);
    setRepeatEnd('never');
    setSelectedItems([]);
  };

  const matchJobs = (arr1: any[], arr2: any[]) => {
    if (arr1.length !== arr2.length) {
      return false;
    }
    const arr2Copy = arr2.slice();
    for (let i = 0; i < arr1.length; i++) {
      const index = arr2Copy.indexOf(arr1[i]);
      if (index === -1) {
        return false;
      }
      arr2Copy.splice(index, 1);
    }
    return true;
  };

  const handleSave = () => {
    const localStart = startTimestamp && dayjs.unix(startTimestamp);
    const startUTC = startTimestamp && dayjs.unix(startTimestamp).utc();
    const localDate = localStart && localStart.format('YYYY-MM-DD');
    const utcDate = startUTC && startUTC.format('YYYY-MM-DD');

    let same = false;
    let comparisonValue = 0;

    if (utcDate === localDate) {
      same = true;
    } else {
      if (utcDate != null && localDate != null) {
        // If dates are different, check which one is greater
        if (utcDate > localDate) {
          comparisonValue = 1; // UTC is greater
        } else if (utcDate < localDate) {
          comparisonValue = 0; // UTC is smaller
        }
      }
    }
    let adjustedDays = [...selectedDays]; // Create a copy of selectedDays

    if (repeat) {
      if (same) {
        // If same, keep days as is
        adjustedDays = selectedDays;
      } else if (comparisonValue === 1) {
        // UTC is smaller, shift all days forward by 1
        adjustedDays = adjustedDays.map((day) => {
          const newDay = (parseInt(day) + 1 + 7) % 7; // Wrap around using modulo
          return newDay.toString(); // Convert back to string
        });
      } else if (comparisonValue === 0) {
        // UTC is greater, shift all days back by 1
        adjustedDays = adjustedDays.map((day) => {
          const newDay = (parseInt(day) - 1) % 7; // Wrap around using modulo
          return newDay.toString(); // Convert back to string
        });
      }
    }
    const payload = {
      block_type: defaultType === 'inPerson' ? 1 : 0,
      start_time: startTimestamp as number,
      end_time: endTimestamp as number,
      eventDate: formattedDate,
      max_spots: numberOfSlots,
      company_id: companyDetail?.company_id,
      repeat_spots: repeat ? 1 : 0,
      ...(repeat && { days: adjustedDays.join(',') }),
      ...(repeat && { original_days: selectedDays.join(',') }),
      ...(repeat && {
        ends_type: repeatEnd === 'on' ? 1 : repeatEnd === 'after' ? 2 : 0,
      }),
      ...(repeat &&
        repeatEnd === 'on' && {
          ends_on: repeateDate?.format('YYYY-MM-DD') ?? '',
        }),
      ...(repeat &&
        repeatEnd === 'after' && { ends_on: Number(repeateOccurence) }),
      ...(repeat && repeatEnd === 'never' && { ends_on: 0 }),
      job_ids:
        selectedItems.length ==
        jobPostingsData?.object.filter(
          (item: any) => item.status !== 1 && item.status !== 4,
        ).length
          ? ''
          : selectedItems?.map((item: { id: any }) => item.id).join(','),
    };

    const payloadTime = {
      block_type: defaultType === 'inPerson' ? 1 : 0,
      start_time: startTimestamp as number,
      end_time: endTimestamp as number,
      eventDate: formattedDate,
      time_period: Number(slotTime),
      company_id: companyDetail?.company_id,
      repeat_spots: repeat ? 1 : 0,
      ...(repeat && { days: adjustedDays.join(',') }),
      ...(repeat && { original_days: selectedDays.join(',') }),
      ...(repeat && {
        ends_type: repeatEnd === 'on' ? 1 : repeatEnd === 'after' ? 2 : 0,
      }),
      ...(repeat &&
        repeatEnd === 'on' && {
          ends_on: repeateDate?.format('YYYY-MM-DD') ?? '',
        }),
      ...(repeat &&
        repeatEnd === 'after' && { ends_on: Number(repeateOccurence) }),
      ...(repeat && repeatEnd === 'never' && { ends_on: 0 }),
      job_ids:
        selectedItems.length ==
        jobPostingsData?.object.filter(
          (item: any) => item.status !== 1 && item.status !== 4,
        ).length
          ? ''
          : selectedItems?.map((item: { id: any }) => item.id).join(','),
    };

    const updatePayload = {
      start_time: startTimestamp as number,
      end_time: endTimestamp as number,
      eventDate: formattedDate,
      company_id: interviewBlock?.company.id,
      ...(value === 'number' && { max_spots: numberOfSlots }),
      ...(value === 'time' && { time_period: Number(slotTime) }),
      job_ids:
        selectedItems.length ==
        jobPostingsData?.object.filter(
          (item: any) => item.status !== 1 && item.status !== 4,
        ).length
          ? ''
          : selectedItems?.map((item: { id: any }) => item.id).join(','),
      delete_job_ids: !matchJobs(
        selectedItems.map((item: { id: any }) => item.id),
        previousJobIds || [],
      ),
      delete_job_slots:
        (interviewBlock &&
          interviewBlock.slots &&
          interviewBlock.slots?.length > 0 &&
          value === 'number') ||
        (interviewBlock && !interviewBlock.slots) ||
        (interviewBlock &&
          interviewBlock.slots &&
          interviewBlock.slots?.length < 1 &&
          value === 'time') ||
        (interviewBlock &&
          interviewBlock.slots &&
          interviewBlock.slots?.length > 0 &&
          calculateTimeDifference(
            interviewBlock?.slots?.[0].slot_start_time,
            interviewBlock?.slots?.[0].slot_end_time,
          ) != slotTime) ||
        formattedDate !==
          dayjs(interviewBlock?.dt_start_time).format('YYYY-MM-DD') ||
        startTimestamp !==
          dayjs(interviewBlock?.dt_start_time).valueOf() / 1000 ||
        endTimestamp !== dayjs(interviewBlock?.dt_end_time).valueOf() / 1000
          ? true
          : false,
      repeat_spots: repeat ? 1 : 0,
      ...(repeat && { days: adjustedDays.join(',') }),
      ...(repeat && { original_days: selectedDays.join(',') }),
      ...(repeat && {
        ends_type: repeatEnd === 'on' ? 1 : repeatEnd === 'after' ? 2 : 0,
      }),
      ...(repeat &&
        repeatEnd === 'on' && {
          ends_on: repeateDate?.format('YYYY-MM-DD') ?? '',
        }),
      ...(repeat &&
        repeatEnd === 'after' && { ends_on: Number(repeateOccurence) }),
      ...(repeat && repeatEnd === 'never' && { ends_on: 0 }),
    };

    const intervalInMinutes = dayjs
      .unix(endTimestamp ? endTimestamp : 0)
      .diff(dayjs.unix(startTimestamp ? startTimestamp : 0), 'minutes');

    if (intervalInMinutes < Number(slotTime)) {
      setErrorText(
        'Interview block duration must be grater than time interval',
      );
      return;
    }
    if (
      repeat &&
      repeatEnd === 'on' &&
      repeateDate &&
      formattedDate >= repeateDate?.format('YYYY-MM-DD')
    ) {
      setErrorText('Repeat end date must be greater than start date');
      return;
    }
    if (selectedItems?.length < 1) {
      setErrorText('Please select at least one job');
      return;
    }
    setErrorText('');

    const options = {
      onSuccess: (data: { status: number; message: string }) => {
        if (data.status === 200) {
          reset();
          handleClose();
          toast.success(data.message);
          onSuccess();
        }
      },
    };

    if (interviewBlock) {
      updateInterviewBlock(
        {
          ...updatePayload,
          calendar_block_id: interviewBlock.id,
          block_type: defaultType === 'inPerson' ? 1 : 0,
        },
        options,
      );
    } else {
      if (value === 'number') {
        createInterviewBlock(payload, options);
      } else if (value === 'time') {
        createInterviewBlockTime(payloadTime, options);
      }
    }
  };

  const handleSlotChange = (event: SelectChangeEvent) => {
    setSlotTime(event?.target.value);
  };

  const handleSwitch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setValue((event.target as HTMLInputElement).value);
  };

  useEffect(() => {
    if (interviewBlock && jobPostingsData) {
      const filteredJobs = jobPostingsData?.object?.filter(
        (item: { id: any; status: number }) =>
          interviewBlock.jobs &&
          interviewBlock?.jobs.some(
            (job) =>
              job.job_listing.id == item.id &&
              item.status !== 1 &&
              item.status !== 4,
          ),
      );
      setSelectedItems([...filteredJobs]);
      setValue(
        interviewBlock?.slots && interviewBlock.slots?.length > 0
          ? 'time'
          : interviewBlock
            ? 'number'
            : 'time',
      );
      if (interviewBlock.slots && interviewBlock.slots?.length > 0) {
        setSlotTime(
          `${calculateTimeDifference(interviewBlock?.slots?.[0]?.slot_start_time, interviewBlock?.slots?.[0]?.slot_end_time)}`,
        );
      }
      setPreviousJobIds(
        interviewBlock.jobs?.map((item) => item.job_listing.id),
      );
    } else if (jobPostingsData) {
      setSelectedItems(
        jobPostingsData?.object.filter(
          (item: any) => item.status !== 1 && item.status !== 4,
        ),
      );
      setSlotTime('15');
      setValue('time');
    }
  }, [
    isOpen,
    interviewBlock,
    companyDetail && companyDetail.company_id,
    jobPostingsData,
    isLoadingJobPostings,
  ]);

  useEffect(() => {
    if (interviewBlock) {
      setNumberOfSlots(interviewBlock?.max_spots ?? 4);
    }
  }, [interviewBlock]);

  const handleCloseModal = () => {
    // reset()
    setRepeat(false);
    setShowRepeatModal(false);
    setRepeatOccurence(null);
    setRepeatDate(null);
    setSelectedDays(['1']);
    setRepeatEnd('never');
    setSelectedItems([]);
    handleClose();
    setDefaultType(
      jobPostingsData?.object[0]?.company?.additional_info
        ?.physical_interview_address === true ||
        jobPostingsData?.object[0]?.company?.additional_info
          ?.physical_interview_address === 'true'
        ? 'inPerson'
        : 'virtual',
    );
  };

  const physical_address_concate =
    jobPostingsData &&
    jobPostingsData?.object?.length > 0 &&
    jobPostingsData?.object[0]?.company?.additional_info &&
    jobPostingsData?.object[0]?.company?.additional_info
      ?.physical_interview_location
      ? jobPostingsData?.object[0]?.company?.additional_info
          .physical_interview_location
      : jobPostingsData && jobPostingsData?.object?.length > 0
        ? `${jobPostingsData?.object[0]?.company?.s_address1 || jobPostingsData?.object[0]?.company?.s_address2 || ''} ${jobPostingsData?.object[0]?.company?.s_city || ''} ${jobPostingsData?.object[0]?.company?.s_state || ''} ${jobPostingsData?.object[0]?.company?.s_zip || ''}`.trim()
        : '';

  useEffect(() => {
    if (
      jobPostingsData &&
      jobPostingsData?.object?.length > 0 &&
      jobPostingsData?.object[0]?.company?.additional_info
    ) {
      setDefaultType(
        jobPostingsData?.object[0]?.company?.additional_info
          ?.physical_interview_address === true ||
          jobPostingsData?.object[0]?.company?.additional_info
            ?.physical_interview_address === 'true'
          ? 'inPerson'
          : 'virtual',
      );
    }
  }, [jobPostingsData?.object[0]?.company?.additional_info]);

  const enterprise_virtual_Link =
    authData.user?.enterprise &&
    defaultType === 'virtual' &&
    !jobPostingsData?.object[0]?.company?.additional_info.virtual_interview_link
      ? true
      : false;

  return (
    <Dialog open={isOpen} onClose={handleCloseModal} fullWidth maxWidth="md">
      <DialogTitle>
        <IconButton
          sx={{ position: 'absolute', top: 5, right: 5 }}
          onClick={handleCloseModal}
        >
          <CloseIcon />
        </IconButton>
        <br />
        <Typography variant="h4">
          {interviewBlock ? (
            <>
              Update Interview Block for <b>{interviewBlock.company.s_name}</b>
            </>
          ) : (
            companyDetail && (
              <>
                Add Interview for <b>{companyDetail.name}</b>
              </>
            )
          )}
        </Typography>
      </DialogTitle>
      <DialogContent>
        {errorText && (
          <Alert severity="error" sx={{ width: '100%' }}>
            {errorText}
          </Alert>
        )}

        <Box>
          <FormControl sx={{ width: '100%', mt: '10px' }}>
            <RadioGroup
              aria-labelledby="demo-controlled-radio-buttons-group"
              name="controlled-radio-buttons-group"
              value={defaultType} // <-- keep it as "inPerson" | "virtual"
              onChange={(e) =>
                setDefaultType?.(e.target.value as 'inPerson' | 'virtual')
              }
              sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}
            >
              <FormControlLabel
                value="inPerson"
                control={<Radio />}
                label="In Person"
              />
              <FormControlLabel
                value="virtual"
                control={<Radio />}
                label="Virtual"
              />
            </RadioGroup>

            {defaultType === 'inPerson' && (
              <Box
                sx={{
                  border: '1px solid #e4ecf2',
                  px: '10px',
                  borderRadius: '10px',
                  py: '10px',
                  pb: '20px',
                }}
              >
                <Typography
                  fontWeight="bold"
                  variant="body2"
                  mt="3px"
                  paddingTop="2px"
                >
                  {jobPostingsData?.object[0]?.company?.s_name}
                </Typography>

                <Typography variant="body2" mt="3px" paddingTop="2px">
                  {physical_address_concate}
                </Typography>

                <Box sx={{ mt: '-10px' }}></Box>
              </Box>
            )}

            {defaultType === 'virtual' && (
              <Box
                sx={{
                  border: '1px solid #e4ecf2',
                  px: '10px',
                  borderRadius: '10px',
                }}
              >
                <Typography
                  color={'text.primary'}
                  fontStyle={
                    jobPostingsData?.object[0]?.company?.additional_info
                      ?.virtual_interview_link
                      ? 'normal'
                      : 'italic'
                  }
                  whiteSpace="pretty"
                  sx={{ wordBreak: 'break-all', mb: '2px' }}
                >
                  {
                    jobPostingsData?.object[0]?.company?.additional_info
                      ?.virtual_interview_link
                  }
                </Typography>

                {!jobPostingsData?.object[0]?.company?.additional_info
                  ?.virtual_interview_link && (
                  <Typography
                    variant="body2"
                    color="red"
                    fontSize="15px"
                    sx={{ pl: 2, py: 2 }}
                  >
                    Please set a virtual interview link at the company level.
                  </Typography>
                )}
              </Box>
            )}
          </FormControl>
        </Box>
        <Stack spacing={6} sx={{ marginBottom: 3 }}>
          <Stack direction="row" spacing={4}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <SelectDateTimeForm
                  repeat={repeat}
                  setRepeat={setRepeat}
                  setStartTimestamp={setStartTimestamp!}
                  setEndTimestamp={setEndTimestamp!}
                  setFormattedDate={setFormattedDate!}
                  setIsPastDateTime={setIsPastDateTime}
                  selectedDays={selectedDays}
                  repeateOccurence={repeateOccurence}
                  setRepeatOccurence={setRepeatOccurence}
                  setSelectedDays={setSelectedDays}
                  repeateDate={repeateDate}
                  repeatEnd={repeatEnd}
                  showRepeatModal={showRepeatModal}
                  setShowRepeatModal={setShowRepeatModal}
                  setRepeatDate={setRepeatDate}
                  setRepeatEnd={setRepeatEnd}
                  defaultDuration={120}
                  directionColumn
                  initialValues={
                    interviewBlock
                      ? {
                          date: dayjs(interviewBlock.dt_start_time),
                          startTime: dayjs(interviewBlock.dt_start_time),
                          endTime: dayjs(interviewBlock.dt_end_time),
                        }
                      : undefined
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl sx={{ width: '100%', m: 1, minWidth: 120 }}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Typography>Select Jobs</Typography>
                    <Tooltip
                      title="Closed positions are not available for interview blocks. To add a position not listed here, move the hiring status from closed to accepting or hiring under Job Position management for this location"
                      placement="top"
                      arrow
                    >
                      <InfoIcon
                        sx={{
                          marginBottom: '2px !important',
                          fontSize: '14px',
                        }}
                        color="secondary"
                      />
                    </Tooltip>
                  </Stack>
                  {jobPostingsData &&
                  jobPostingsData?.object.filter(
                    (item: any) => item.status !== 1 && item.status !== 4,
                  ).length > 0 ? (
                    <Select
                      size="small"
                      sx={{ width: '100%' }}
                      labelId="multi-select-label"
                      id="multi-select"
                      multiple
                      value={selectedItems}
                      onChange={handleJobChange}
                      renderValue={(selected) => (
                        <Box
                          sx={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}
                        >
                          {selected.map(
                            (value: {
                              id: Key | null | undefined;
                              s_title: string;
                            }) => (
                              <Chip
                                sx={{
                                  m: 0,
                                  p: 0,
                                  height: '28px',
                                  fontSize: '0.8rem',
                                  borderRadius: 5,
                                }}
                                key={value.id}
                                label={value.s_title}
                              />
                            ),
                          )}
                        </Box>
                      )}
                    >
                      {/* Add Select All MenuItem */}
                      <MenuItem value="select-all" onClick={handleSelectAll}>
                        {selectedItems.length ===
                        jobPostingsData?.object.filter(
                          (item: any) => item.status !== 1 && item.status !== 4,
                        ).length
                          ? 'Deselect All'
                          : 'Select All'}
                      </MenuItem>

                      {jobPostingsData &&
                        jobPostingsData?.object
                          .filter(
                            (item: any) =>
                              item.status !== 1 && item.status !== 4,
                          )
                          .map((job: any) => (
                            <MenuItem key={job.id} value={job}>
                              {job.s_title}
                            </MenuItem>
                          ))}
                    </Select>
                  ) : !isLoadingJobPostings ? (
                    <Typography
                      variant="body2"
                      sx={{ p: 1, color: 'red', fontStyle: 'italic' }}
                    >
                      No active jobs available. To add a position not listed
                      here, move the hiring status from closed to accepting or
                      hiring under Job Position management for this location
                    </Typography>
                  ) : (
                    isLoadingJobPostings && <CircularProgress />
                  )}
                </FormControl>

                <FormControl>
                  <RadioGroup
                    aria-labelledby="demo-controlled-radio-buttons-group"
                    name="controlled-radio-buttons-group"
                    value={value}
                    onChange={handleSwitch}
                    sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}
                  >
                    <FormControlLabel
                      value="time"
                      control={<Radio size="small" />}
                      label={
                        <>
                          By Time
                          <Tooltip
                            title="Set up interviews by time to allow applicants to select specific times within your block."
                            arrow
                          >
                            <IconButton size="small" sx={{ ml: 0.5 }}>
                              <InfoIcon
                                sx={{
                                  marginBottom: '2px !important',
                                  fontSize: '14px',
                                }}
                                color="secondary"
                              />
                            </IconButton>
                          </Tooltip>
                        </>
                      }
                    />
                    <FormControlLabel
                      value="number"
                      control={<Radio size="small" />}
                      label={
                        <>
                          Open Interview
                          <Tooltip
                            title="Setting up Open Interviews allows groups of applicants to sign up and arrive at the start time of the block you've chosen, or anytime within the block you’ve chosen if that's your preference"
                            arrow
                          >
                            <IconButton size="small" sx={{ ml: 0.5 }}>
                              <InfoIcon
                                sx={{
                                  marginBottom: '2px !important',
                                  fontSize: '14px',
                                }}
                                color="secondary"
                              />
                            </IconButton>
                          </Tooltip>
                        </>
                      }
                    />
                  </RadioGroup>
                </FormControl>
                {value === 'time' ? (
                  <Box sx={{ width: '100%' }}>
                    <Typography>Interview Duration</Typography>
                    <Select
                      size="small"
                      value={slotTime}
                      onChange={handleSlotChange}
                      displayEmpty
                      inputProps={{ 'aria-label': 'Without label' }}
                      sx={{
                        width: '100%',
                      }}
                      MenuProps={{ PaperProps: { style: { borderRadius: 5 } } }}
                    >
                      <MenuItem value={10}>10 minutes</MenuItem>
                      <MenuItem value={15}>15 minutes</MenuItem>
                      <MenuItem value={30}>30 minutes</MenuItem>
                      <MenuItem value={45}>45 minutes</MenuItem>
                      <MenuItem value={60}>60 minutes</MenuItem>
                    </Select>
                  </Box>
                ) : value === 'number' ? (
                  <Box>
                    <Typography>Interview Slots:</Typography>
                    <TextField
                      size="small"
                      type="number"
                      placeholder="Number of slots"
                      value={numberOfSlots ?? undefined}
                      onChange={(e) =>
                        updateNumberOfSlots(Number(e.target.value))
                      }
                      InputProps={{ inputProps: { min: 1 } }}
                      fullWidth
                      sx={{
                        width: '100%',
                      }}
                    />
                  </Box>
                ) : null}
              </Grid>
            </Grid>
          </Stack>
          <LoadingButton
            disabled={
              enterprise_virtual_Link ||
              !startTimestamp ||
              !endTimestamp ||
              !formattedDate ||
              !numberOfSlots ||
              isCreatingInterviewBlockTime ||
              isCreatingInterviewBlock ||
              isUpdatingInterviewBlock ||
              isPastDateTime
            }
            variant="contained"
            loadingPosition="start"
            sx={{ width: '50%', alignSelf: 'center' }}
            loading={
              isCreatingInterviewBlock ||
              isCreatingInterviewBlockTime ||
              isUpdatingInterviewBlock
            }
            startIcon={<DoneIcon />}
            onClick={handleSave}
          >
            {interviewBlock
              ? 'Update Calendar Interviews'
              : 'Publish Calendar Interviews'}
          </LoadingButton>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default InterviewBlockModal;
